import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import useAutocomplete from "@mui/material/useAutocomplete";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import SearchIcon from "@mui/icons-material/Search";
import { shouldBlockUnsafeSearchInput } from "@/helpers/sanitizeHelper";
import { primaryBadgeSx } from "../styles/badgeSx";
import { FOCUS_BORDER } from "@/components/muiCustom/styles/focusBorder";

type SearchableOption = Record<string, any>;
type MuiSearchableSelectProps = Record<string, any> & {
  options?: SearchableOption[];
  value?: any;
  onChange?: (..._args: any[]) => void;
};

const SearchTextField = TextField as React.ElementType;
const SelectButton = styled(Box as any, {
  shouldForwardProp: (prop) => !["error", "active"].includes(String(prop)),
})(({ theme, disabled, error, active }: any) => {
  const disabledBorderColor = (theme.vars || theme).palette.action
    .disabledBackground;

  return {
    alignItems: "center",
    backgroundColor: disabled
      ? (theme.vars || theme).palette.action.disabledBackground
      : (theme.vars || theme).palette.background.paper,
    border: `1px solid ${
      error
        ? (theme.vars || theme).palette.error.main
        : disabled
          ? disabledBorderColor
          : "var(--neutral-500)"
    }`,
    borderRadius: "var(--radius-control)",
    boxSizing: "border-box",
    color: disabled
      ? (theme.vars || theme).palette.text.disabled
      : (theme.vars || theme).palette.text.primary,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    justifyContent: "space-between",
    minHeight: 40,
    outline: 0,
    padding: "5px 10px",
    textAlign: "left",
    width: "100%",
    ...(!error && active && FOCUS_BORDER),
    ...(!error && {
      "&:hover": {
        borderColor: disabled
          ? disabledBorderColor
          : active
            ? "var(--primary-base)"
            : "var(--neutral-600)",
      },
      "&:focus-visible": FOCUS_BORDER,
    }),
    "& .MuiSvgIcon-root": {
      color: disabled
        ? (theme.vars || theme).palette.action.disabled
        : (theme.vars || theme).palette.text.primary,
      transition: theme.transitions.create(["transform"], {
        duration: theme.transitions.duration.shortest,
      }),
    },
    "&[aria-expanded='true'] .MuiSvgIcon-root": {
      transform: "rotate(180deg)",
    },
    "& .MuiTypography-root": {
      color: "inherit",
    },
    // Default badge colors live on the Chip (primaryBadgeSx).
    "& .MuiChip-label": {
      color: "inherit",
    },
  };
});

const PopupPaper = styled(Paper)(() => ({
  borderRadius: "var(--radius-container)",
  boxShadow: "var(--shadow-floating)",
  display: "flex",
  flexDirection: "column",
  marginTop: 6,
  maxHeight: 320,
  overflow: "hidden",
}));

const PopupHeader = styled(Box)(({ theme }) => ({
  backgroundColor: (theme.vars || theme).palette.background.paper,
  borderBottom: `1px solid ${(theme.vars || theme).palette.divider}`,
  flex: "0 0 auto",
  padding: theme.spacing(1),
  position: "relative",
  zIndex: 1,
}));

const OptionList = styled("ul")(({ theme }) => ({
  listStyle: "none",
  margin: 0,
  flex: "1 1 auto",
  maxHeight: 240,
  minHeight: 0,
  overflowY: "auto",
  padding: theme.spacing(0.5, 0),
  position: "relative",
  scrollPaddingBlock: theme.spacing(0.5),
  "& li": {
    alignItems: "center",
    cursor: "pointer",
    display: "flex",
    minHeight: 36,
    padding: theme.spacing(0.5, 1.5),
    position: "relative",
  },
  "& li[aria-selected='true']": {
    backgroundColor: (theme.vars || theme).palette.action.selected,
    color: (theme.vars || theme).palette.text.primary,
    fontWeight: 600,
    "& .MuiTypography-root": {
      fontWeight: 600,
    },
    "& .MuiListItemText-primary": {
      fontWeight: 600,
    },
  },
  "& li.Mui-focused": {
    backgroundColor: (theme.vars || theme).palette.action.hover,
  },
  "& li[aria-selected='true'].Mui-focused": {
    backgroundColor: "var(--primary-tint-hover)",
  },
  "& li[aria-disabled='true']": {
    color: (theme.vars || theme).palette.text.disabled,
    cursor: "not-allowed",
  },
}));

const HiddenAutocompleteInput = styled("input")({
  border: 0,
  height: 0,
  opacity: 0,
  padding: 0,
  pointerEvents: "none",
  position: "absolute",
  width: 0,
});

const getDefaultOptionLabel = (option: SearchableOption, labelKey: string) =>
  option?.[labelKey] ?? "";
const getDefaultOptionValue = (option: SearchableOption, valueKey: string) =>
  option?.[valueKey];

const setRef = (ref: any, value: HTMLInputElement | null) => {
  if (!ref) return;

  if (typeof ref === "function") {
    ref(value);
    return;
  }

  ref.current = value;
};

const MuiSearchableSelect = ({
  options = [],
  value,
  onChange,
  multiple = false,
  label = "",
  name = "searchable-select",
  placeholder = "Select",
  searchPlaceholder = "Search..",
  helperText = "",
  error = false,
  required = false,
  disabled = false,
  fullWidth = true,
  size = "small",
  labelKey = "label",
  valueKey = "value",
  limitTags = 1,
  disableClearable = false,
  selectAllText = "Select all",
  clearAllText = "Clear all",
  noOptionsText = "No options",
  getOptionLabel,
  getOptionValue,
  getOptionDisabled,
  renderOption,
  renderValue,
  onInputChange,
  popperProps = {},
  listboxProps = {},
  searchInputProps = {},
  selectButtonProps = {},
  wrapperProps = {},
}: MuiSearchableSelectProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  const optionLabel = useCallback(
    (option) =>
      getOptionLabel
        ? getOptionLabel(option)
        : getDefaultOptionLabel(option, labelKey),
    [getOptionLabel, labelKey]
  );
  const optionValue = useCallback(
    (option) =>
      getOptionValue
        ? getOptionValue(option)
        : getDefaultOptionValue(option, valueKey),
    [getOptionValue, valueKey]
  );
  const optionKey = useCallback(
    (option: SearchableOption) => optionValue(option) ?? optionLabel(option),
    [optionLabel, optionValue]
  );
  const isOptionDisabled = useCallback(
    (option) => Boolean(getOptionDisabled?.(option)),
    [getOptionDisabled]
  );

  const selectedOptions = useMemo(() => {
    if (multiple) {
      const selectedValues = Array.isArray(value) ? value : [];
      return options.filter((option) =>
        selectedValues.includes(optionValue(option))
      );
    }

    return options.find((option) => optionValue(option) === value) || null;
  }, [multiple, optionValue, options, value]);

  const isOptionEqualToValue = (
    option: SearchableOption,
    selectedOption: SearchableOption
  ) => optionValue(option) === optionValue(selectedOption);

  const closePopup = () => {
    setOpen(false);
    setInputValue("");
  };

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: any,
    reason: string,
    details: any
  ) => {
    if (multiple) {
      const enabledValues = Array.isArray(newValue)
        ? newValue
            .filter((option) => !isOptionDisabled(option))
            .map((option) => optionValue(option))
        : [];

      onChange?.(event, enabledValues, reason, details);
      return;
    }

    if (newValue && isOptionDisabled(newValue)) return;

    onChange?.(event, newValue ? optionValue(newValue) : "", reason, details);
    setInputValue("");
  };

  const {
    anchorEl,
    getInputProps,
    getListboxProps,
    getOptionProps,
    getRootProps,
    groupedOptions,
    setAnchorEl,
  } = useAutocomplete({
    disabled,
    disableClearable,
    disableCloseOnSelect: multiple,
    getOptionDisabled,
    getOptionKey: optionKey,
    getOptionLabel: optionLabel,
    inputValue,
    isOptionEqualToValue,
    multiple,
    onChange: handleChange,
    onClose: () => {
      closePopup();
    },
    onInputChange: (event, nextInputValue, reason) => {
      if (!multiple && reason !== "input") {
        setInputValue("");
        onInputChange?.(event, "", reason);
        return;
      }

      if (reason === "input" && shouldBlockUnsafeSearchInput(nextInputValue)) {
        return;
      }

      setInputValue(nextInputValue);
      onInputChange?.(event, nextInputValue, reason);
    },
    onOpen: () => setOpen(true),
    open,
    openOnFocus: true,
    options,
    value: selectedOptions,
  });

  useEffect(() => {
    if (!open) return;

    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [open]);

  const selectedCount = multiple
    ? Array.isArray(selectedOptions)
      ? selectedOptions.length
      : 0
    : selectedOptions
      ? 1
      : 0;

  const allEnabledOptions = useMemo(
    () => options.filter((option) => !isOptionDisabled(option)),
    [isOptionDisabled, options]
  );

  const selectedEnabledCount = multiple
    ? selectedOptions.filter((option) => !isOptionDisabled(option)).length
    : selectedCount;
  const allSelected =
    multiple &&
    allEnabledOptions.length > 0 &&
    selectedEnabledCount === allEnabledOptions.length;

  const handleSelectAll = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange?.(
      event,
      allSelected ? [] : allEnabledOptions.map((option) => optionValue(option)),
      allSelected ? "clear" : "selectAll"
    );
  };

  const handleClearAll = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onChange?.(event, multiple ? [] : "", "clear");
    if (!multiple) {
      closePopup();
    }
  };

  const handleClickAway = (event: any) => {
    if (anchorEl?.contains(event.target)) return;
    closePopup();
  };

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
  };

  const {
    onClick: selectButtonOnClick,
    onKeyDown: selectButtonOnKeyDown,
    onMouseDown: selectButtonOnMouseDown,
    ...restSelectButtonProps
  } = selectButtonProps;

  const handleButtonClick = (event: React.MouseEvent) => {
    selectButtonOnClick?.(event);
    if (event.defaultPrevented) return;
    handleOpen();
  };

  const handleButtonMouseDown = (event: React.MouseEvent) => {
    selectButtonOnMouseDown?.(event);
    if (event.defaultPrevented) return;

    if (open) {
      event.preventDefault();
    }
  };

  const handleButtonKeyDown = (event: React.KeyboardEvent) => {
    selectButtonOnKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (disabled) return;
    if (["ArrowDown", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
    }
  };

  const inputProps = getInputProps();
  const { ref: autocompleteInputRef, ...autocompleteInputProps } = inputProps;
  const visibleAutocompleteInputProps = { ...autocompleteInputProps };
  delete visibleAutocompleteInputProps.onMouseDown;
  const autocompleteRootProps = getRootProps();
  const listboxAutocompleteProps = getListboxProps();
  const displayOptions = Array.isArray(groupedOptions) ? groupedOptions : [];
  const {
    InputProps: searchInputSlotProps = {},
    inputProps: searchHtmlInputSlotProps = {},
    inputRef: searchInputRef,
    slotProps: searchSlotProps = {},
    onKeyDown: searchInputKeyDown,
    ...textFieldProps
  } = searchInputProps;

  const handleSearchInputKeyDown = (event: any) => {
    searchInputKeyDown?.(event);
    const isEmptyDelete =
      multiple &&
      inputValue === "" &&
      (event.key === "Backspace" || event.key === "Delete");

    if (isEmptyDelete) {
      event.stopPropagation();
      return;
    }

    if (!event.defaultPrevented) {
      autocompleteRootProps.onKeyDown?.(event);
    }
  };

  const defaultDisplay = () => {
    if (renderValue) {
      return renderValue(selectedOptions);
    }

    if (!selectedCount) {
      return (
        <Typography color="text.secondary" fontSize=".8rem">
          {placeholder}
        </Typography>
      );
    }

    if (!multiple) {
      return (
        <Typography fontSize=".8rem" fontWeight={600} noWrap>
          {optionLabel(selectedOptions)}
        </Typography>
      );
    }

    const visibleOptions = selectedOptions.slice(0, limitTags);

    return (
      <Box alignItems="center" display="flex" gap={0.5} minWidth={0}>
        {visibleOptions.map((option) => (
          <Chip
            key={optionValue(option)}
            label={optionLabel(option)}
            size="small"
            sx={[
              primaryBadgeSx,
              {
                borderRadius: "var(--radius-control)",
                fontSize: ".7rem",
                height: 24,
                maxWidth: 140,
              },
            ]}
          />
        ))}
        {selectedCount > visibleOptions.length && (
          <Typography fontSize=".75rem" fontWeight={700} noWrap>
            +{selectedCount - visibleOptions.length} selected
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      error={error}
      required={required}
      disabled={disabled}
      {...wrapperProps}
    >
      <HiddenAutocompleteInput
        {...autocompleteInputProps}
        ref={autocompleteInputRef}
        tabIndex={-1}
      />
      {label && (
        <FormLabel
          id={`${name}-label`}
          sx={{ fontSize: ".85rem", fontWeight: 600, mb: 0.5 }}
        >
          {label}
        </FormLabel>
      )}
      <Box ref={setAnchorEl}>
        <SelectButton
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-labelledby={label ? `${name}-label` : undefined}
          component="button"
          disabled={disabled}
          error={error ? 1 : 0}
          active={open ? 1 : 0}
          onClick={handleButtonClick}
          onKeyDown={handleButtonKeyDown}
          onMouseDown={handleButtonMouseDown}
          type="button"
          {...restSelectButtonProps}
        >
          <Box minWidth={0} overflow="hidden">
            {defaultDisplay()}
          </Box>
          <ArrowDropDownIcon color={disabled ? "disabled" : "inherit"} />
        </SelectButton>
      </Box>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
        modifiers={[
          {
            name: "sameWidth",
            enabled: true,
            phase: "beforeWrite",
            requires: ["computeStyles"],
            fn: ({ state }: any) => {
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
          },
        ]}
        {...popperProps}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <PopupPaper>
            <PopupHeader>
              {(multiple || (!disableClearable && selectedCount > 0)) && (
                <Box
                  display="flex"
                  justifyContent={multiple ? "space-between" : "flex-end"}
                  mb={1}
                >
                  {multiple && (
                    <Button
                      disabled={!allEnabledOptions.length}
                      onMouseDown={handleSelectAll}
                      size="small"
                      sx={{
                        fontSize: ".7rem",
                        minWidth: 0,
                        px: 1,
                        textTransform: "none",
                      }}
                    >
                      {selectAllText}
                    </Button>
                  )}
                  <Button
                    disabled={!selectedCount}
                    onMouseDown={handleClearAll}
                    size="small"
                    sx={{
                      fontSize: ".7rem",
                      minWidth: 0,
                      px: 1,
                      textTransform: "none",
                    }}
                  >
                    {clearAllText}
                  </Button>
                </Box>
              )}
              <SearchTextField
                {...textFieldProps}
                fullWidth
                inputRef={(node) => {
                  inputRef.current = node;
                  setRef(searchInputRef, node);
                  setRef(searchSlotProps.htmlInput?.ref, node);
                }}
                name={`${name}-search`}
                onKeyDown={handleSearchInputKeyDown}
                placeholder={searchPlaceholder}
                size={size}
                type="search"
                value={inputValue}
                slotProps={{
                  ...searchSlotProps,
                  input: {
                    ...searchInputSlotProps,
                    ...searchSlotProps.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                    sx: {
                      minHeight: 32,
                      height: 32,
                      ...searchInputSlotProps.sx,
                      ...searchSlotProps.input?.sx,
                    },
                  },
                  htmlInput: {
                    ...visibleAutocompleteInputProps,
                    ...searchHtmlInputSlotProps,
                    ...searchSlotProps.htmlInput,
                    id: `${name}-visible-search`,
                    ref: undefined,
                    style: {
                      fontSize: "0.75rem",
                      paddingBottom: 4,
                      paddingTop: 4,
                      ...searchHtmlInputSlotProps.style,
                      ...searchSlotProps.htmlInput?.style,
                    },
                  },
                }}
                sx={{
                  "& .MuiInputAdornment-root svg": {
                    fontSize: "1rem",
                  },
                  ...textFieldProps.sx,
                }}
              />
            </PopupHeader>

            {displayOptions.length > 0 ? (
              <OptionList {...listboxAutocompleteProps} {...listboxProps}>
                {displayOptions.map((option: SearchableOption, index) => {
                  const optionProps = getOptionProps({ option, index });
                  const {
                    key,
                    onClick: optionOnClick,
                    onMouseDown: optionOnMouseDown,
                    ...restOptionProps
                  } = optionProps;
                  const optionDisabled = isOptionDisabled(option);
                  const selected = multiple
                    ? selectedOptions.some((selectedOption) =>
                        isOptionEqualToValue(option, selectedOption)
                      )
                    : selectedOptions
                      ? isOptionEqualToValue(option, selectedOptions)
                      : false;
                  const handleOptionMouseDown = (event: any) => {
                    if (optionDisabled) {
                      event.preventDefault();
                      event.stopPropagation();
                      return;
                    }

                    optionOnMouseDown?.(event);
                  };
                  const handleOptionClick = (event: any) => {
                    if (optionDisabled) {
                      event.preventDefault();
                      event.stopPropagation();
                      return;
                    }

                    optionOnClick?.(event);
                  };

                  return (
                    <li
                      key={key}
                      {...restOptionProps}
                      onClick={handleOptionClick}
                      onMouseDown={handleOptionMouseDown}
                    >
                      {multiple && (
                        <Checkbox
                          checked={selected}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          disabled={optionDisabled}
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          size="small"
                          sx={{ mr: 1, p: 0.25 }}
                        />
                      )}
                      {renderOption ? (
                        renderOption(option, { selected, inputValue })
                      ) : (
                        <ListItemText
                          primary={optionLabel(option)}
                          slotProps={{
                            primary: {
                              sx: {
                                fontSize: ".8rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                            },
                          }}
                        />
                      )}
                    </li>
                  );
                })}
              </OptionList>
            ) : (
              <Typography
                color="text.secondary"
                fontSize=".8rem"
                px={2}
                py={1.5}
              >
                {noOptionsText}
              </Typography>
            )}
          </PopupPaper>
        </ClickAwayListener>
      </Popper>

      {helperText && (
        <FormHelperText sx={{ mx: 0 }}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export { MuiSearchableSelect };
export default MuiSearchableSelect;
